import './index.css';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../actions/app-actions-types';
import { addAddress, updateAddress } from '../../actions/user-actions-types';
import useTheme from '../../utilities/hooks/theme';
import I18n from '../../utilities/locale';
import Regex from '../../utilities/Regex';
import Constants from '../../constants';
import Card from '../card';
import View from '../view';
import Text from '../text';
import Button from '@mui/material/Button/Button';
import TextField from '@mui/material/TextField/TextField';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText/FormHelperText';
import React from 'react';

interface Props {
  list: any;
  onAddUpdateAddress: any;
}
const VerifyAddressItem = ({ list = [], onAddUpdateAddress }: Props) => {
  const { ComponentTheme: theme } = useTheme();
  const dispatch = useDispatch();
  const [expand, setExpand] = useState(false);

  const refStreet = useRef(null as any);
  const refCity = useRef(null as any);
  const refZip = useRef(null as any);

  const [street, setStreet] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  const [streetError, setStreetError] = useState(null);
  const [cityError, setCityError] = useState(null);
  const [stateError, setStateError] = useState(null);
  const [zipError, setZipError] = useState(null);

  const [isNeedToEdit, setIsNeedToEdit] = useState(false);
  const [address = {}] = list;

  useEffect(() => {
    const { state = '', street = '', city = '', zip = '', id = '' } = address;
    setCity(city);
    setZip(zip);
    setStreet(street);
    setStreetAddress(street);
    setState(state);
    setIsNeedToEdit(id.length > 0);
  }, [list]);

  const onChangeStreet = (event: ChangeEvent<HTMLInputElement>) => {
    setStreet(event.target.value);
    if (streetError != null) setStreetError(null);
  };

  const onChangeCity = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateName(event.target.value) || event.target.value === '') {
      setCity(event.target.value);
      if (cityError != null) setCityError(null);
    }
  };

  const onChangeZip = (event: ChangeEvent<HTMLInputElement>) => {
    if (Regex.validateNumber(event.target.value) || event.target.value === '') {
      setZip(event.target.value);
      if (zipError != null) setZipError(null);
    }
  };

  const onPressSubmit = () => {
    let streetError = null,
      cityError = null,
      zipError = null,
      stateError = null,
      error = false;

    if (street === '') {
      streetError = I18n.t('MissingStreet');
      error = true;
    }

    if (city === '') {
      cityError = I18n.t('MissingCity');
      error = true;
    }

    if (state === '') {
      stateError = I18n.t('ChooseState');
      error = true;
    }

    if (zip === '') {
      zipError = I18n.t('MissingZipCode');
      error = true;
    } else if (!Regex.validatePIN_Code(zip)) {
      zipError = I18n.t('InvalidZipCode');
      error = true;
    }

    if (error) {
      setStreetError(streetError);
      setCityError(cityError);
      setStateError(stateError);
      setZipError(zipError);
    } else {
      if (isNeedToEdit) {
        updateAddressProceed();
      } else {
        addAddressProceed();
      }
    }
  };

  const addAddressProceed = () => {
    const data = {
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim(),
      default: true
    };
    dispatch(
      addAddress({
        data,
        callback: ({ error }: { error: any }) => {
          if (!error) {
            setExpand(false);
            onAddUpdateAddress();
            dispatch(showToast(I18n.t('AddressAdded')));
          }
        }
      })
    );
  };

  const updateAddressProceed = () => {
    const data = {
      ...address,
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      zip: zip.trim()
    };
    delete data.createdOn;
    dispatch(
      updateAddress({
        data,
        callback: ({ error }: { error: any }) => {
          if (!error) {
            setExpand(false);
            onAddUpdateAddress();
            dispatch(showToast(I18n.t('AddressUpdated')));
          }
        }
      })
    );
  };

  const IndianStatesArray = Constants.IndianStates;

  const onChangeState = (event: SelectChangeEvent) => {
    setState(event.target.value as string);
  };

  const container = {
    borderWidth: streetAddress.length > 0 ? '1.5px' : 0,
    backgroundColor: theme.verifyAccountItemContainer,
    borderColor: theme.verifyAccountItemContainerBorder
  };

  const headerContainer = {
    backgroundColor: theme.verifyAccountItemHeaderContainer
  };

  const headerTitle = {
    color: theme.verifyAccountItemTitle
  };

  const headerVerified = {
    color: theme.verifyAccountItemVerified
  };

  const verifyBtn = {
    color: theme.verifyAccountItemBtnVerifyBtnLabel,
    backgroundColor: theme.verifyAccountItemBtnVerifyBtnBG,
    borderColor: theme.verifyAccountItemBtnVerifyBtnBorder
  };

  const textInputBG = {
    backgroundColor: theme.verifyAccountItemInputBG,
    color: theme.verifyAccountItemInputText
  };

  const bottomBtn = {
    color: theme.verifyAccountItemBottomBtnLabel,
    backgroundColor: theme.verifyAccountItemBottomBtnBG,
    borderColor: theme.verifyAccountItemBottomBtnBorder
  };

  const contentWrapper = {
    backgroundColor: theme.verifyAccountItemContent
  };

  const inputSx: SxProps<Theme> = {
    '& .MuiInputBase-input': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiSelect-select': {
      fontSize: '1.1rem',
      color: theme.verifyAccountItemInputText,
      backgroundColor: theme.verifyAccountItemContent,
      borderColor: theme.verifyAccountItemInputText,
      borderWidth: '1px'
    },
    '& .MuiOutlinedInput-input': {
      color: theme.verifyAccountItemInputText,
      backgroundColor: theme.verifyAccountItemContent
    },
    '& .MuiSvgIcon-root ': {
      color: theme.verifyAccountItemInputText
    },
    '& .MuiFormHelperText-root.Mui-error': {
      backgroundColor: theme.verifyAccountItemContent,
      margin: 0,
      color: theme.verifyAccountItemErrorText,
      paddingLeft: '5px',
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.verifyAccountItemInputText,
      fontSize: '1.1rem',
      fontFamily: 'ProximaNova-Regular',
      textTransform: 'none'
    },
    '& input::placeholder': {
      color: theme.verifyAccountItemInputPlaceHolder,
      fontSize: '1.1rem',
      textTransform: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&:hover fieldset': {
        borderColor: theme.verifyAccountItemInputText
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.verifyAccountItemInputText,
        borderWidth: '1px'
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
      borderColor: theme.verifyAccountItemInputText
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.verifyAccountItemInputText,
      borderWidth: '1px'
    }
  };
  return (
    <Card className={'verify-address-item-container'} style={container}>
      <View className={'verify-address-item-header-container'} style={headerContainer}>
        <View className={'verify-address-item-logo-title-wrapper'}>
          <Constants.Svgs.Pancard
            fillPrimary={theme.svgPrimary}
            fillSecondary={theme.svgSecondary}
          />
          <Text className={'verify-address-item-title'} style={headerTitle}>
            {'Address'}
          </Text>
        </View>
        <View style={{ flexDirection: 'row' }}>
          {streetAddress.length > 0 && (
            <View className={'verify-address-item-logo-title-wrapper'}>
              <Constants.Svgs.VerificationCheck
                width={25}
                height={25}
                fillPrimary={theme.profileIndicatorCheckedPrimary}
                fillSecondary={theme.profileIndicatorCheckedSecondary}
              />
              <Text className={'verify-address-item-verified'} style={headerVerified}>
                {'Verified'}
              </Text>
            </View>
          )}

          <Button
            className={'verify-address-item-verify-btn'}
            style={verifyBtn}
            onClick={() => {
              setExpand(!expand);
            }}>
            {isNeedToEdit ? 'Edit' : 'Add'}
          </Button>
        </View>
      </View>
      {expand && (
        <View className={'verify-address-item-content-wrapper'} style={contentWrapper}>
          <View style={{ width: '80%' }}>
            <TextField
              ref={refStreet}
              style={textInputBG}
              placeholder={I18n.t('Street')}
              value={street}
              className={'verify-bank-item-text-input'}
              label={I18n.t('Street')}
              variant="outlined"
              size={'small'}
              onChange={onChangeStreet}
              sx={inputSx}
              error={streetError ? true : false}
              helperText={streetError}
              inputProps={{ textTransform: 'none' }}
            />
            <TextField
              ref={refCity}
              style={textInputBG}
              placeholder={I18n.t('City')}
              value={city}
              className={'verify-bank-item-text-input'}
              label={I18n.t('City')}
              variant="outlined"
              size={'small'}
              onChange={onChangeCity}
              sx={inputSx}
              error={cityError ? true : false}
              helperText={cityError}
            />
            <FormControl
              style={{
                marginTop: '10px'
              }}
              size={'small'}
              fullWidth
              error={stateError ? true : false}>
              <InputLabel
                style={{
                  fontSize: '1.1rem',
                  fontFamily: 'ProximaNova-Regular',
                  color: theme.verifyAccountItemInputText
                }}>
                {'State'}
              </InputLabel>
              <Select value={state} label="State" sx={inputSx} onChange={onChangeState}>
                {IndianStatesArray.map((item: any) => {
                  return (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{stateError}</FormHelperText>
            </FormControl>
            <TextField
              ref={refZip}
              style={textInputBG}
              placeholder={I18n.t('Zip')}
              value={zip}
              className={'verify-bank-item-text-input'}
              label={I18n.t('Zip')}
              variant="outlined"
              size={'small'}
              onChange={onChangeZip}
              sx={inputSx}
              error={zipError ? true : false}
              helperText={zipError}
              inputProps={{ maxLength: 6 }}
              type="tel"
            />
            <View style={{ width: '100%', alignItems: 'center' }}>
              <Button
                className={'verify-address-item-button'}
                style={bottomBtn}
                onClick={onPressSubmit}>
                {I18n.t('Submit')}
              </Button>
            </View>
          </View>
        </View>
      )}
    </Card>
  );
};
export default React.memo(VerifyAddressItem);
